import type { UpdateAirportTransferPayload } from 'api/experiences'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import type { UpdateAirportTransferFormFields } from '../types'
import moment from 'moment'
import { DMY_CASUAL_LONG_FORMAT, SHORT_TIME_FORMAT_24_HOUR, SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export function formatTransferTravellers(
  travellers: App.OrderTransferItemTraveller,
) {
  let travellerText = formatOccupantsShort({ adults: travellers.adults, children: travellers.children })
  if (travellers.children > 0 && (travellers.childSeats > 0 || travellers.boosterSeats > 0)) {
    const intl = getCurrentIntl()
    const seats = [
      travellers.childSeats > 0 ? intl.formatMessage(
        {
          id: 'dJ10dHFyVK1VciOaNqxm',
          defaultMessage: '{count, plural, one {# child seat} other {# child seats}}',
          description: 'Child seat count in airport transfer traveller summary',
        },
        { count: travellers.childSeats },
      ) : undefined,
      travellers.boosterSeats > 0 ? intl.formatMessage(
        {
          id: 'J6KndjpVUE9pjSpYny6v',
          defaultMessage: '{count, plural, one {# booster seat} other {# booster seats}}',
          description: 'Booster seat count in airport transfer traveller summary',
        },
        { count: travellers.boosterSeats },
      ) : undefined,
    ].filter(Boolean)
    travellerText += ` (${seats.join(', ')})`
  }
  return travellerText
}

export function buildAirportTransferPayload(fields: UpdateAirportTransferFormFields) {
  const { flightNumber, date, time, specialRequests, travellers, baggage, fromLocationName, fromLocationId, toLocationName, toLocationId, surchargeAUD, surchargeIDR } = fields
  const { adults, children, childSeats, boosterSeats } = travellers
  const { bags, oversizedBags } = baggage

  const payload: UpdateAirportTransferPayload = {
    op: 'update_airport_transfer',
    data: {
      flightNumber,
      fromLocationName,
      fromLocationId,
      toLocationName,
      toLocationId,
      day: moment(date).format('YYYY-MM-DD'),
      time,
      specialRequests,
      adults,
      children,
      childSeats,
      boosterSeats,
      bags,
      oversizedBags,
      surchargeAUD,
      surchargeIDR,
    },
  }

  return payload
}

export function airportTransfersUpdateRequired(airportTransfers: Array<App.OrderTransferItem>, experiences?: Record<string, App.ExperienceOffer>) {
  return airportTransfers.some((airportTransfer) => {
    const experience = experiences?.[airportTransfer.experienceId]
    return airportTransferInfoMissing(airportTransfer, experience)
  })
}

export function airportTransferInfoMissing(airportTransfer: App.OrderTransferItem, experience?: App.ExperienceOffer) {
  const isBaliTransfer = isBaliTransferSlug(experience?.slug)
  const isHotelToHotel = isBaliHotelToHotelTransfer(isBaliTransfer, airportTransfer.airport.code)
  return (
    (!isHotelToHotel && !airportTransfer.flightNumber) ||
    !airportTransfer.date ||
    !airportTransfer.time
  )
}

// If there are airport transfers and there are some after today we can update them.
export function isAirportTransferAfterToday(airportTransfers: Array<App.OrderTransferItem>) {
  const currentDate = moment()
  return airportTransfers.some((transfer) => {
    const targetDate = moment(transfer.date)
    return targetDate.isAfter(currentDate)
  })
}

// For the new Complimentary Experiences, we do not want to show cancelled items.
// But we do for want to show them for standalone purchased experiences.
export function filterAirportTransfers(airportTransfers: Array<App.OrderTransferItem>, includeComplimentary = true, includeCancelled = false): Array<App.OrderTransferItem> {
  return airportTransfers.filter(item =>
    item.accommodation_parent_item_id === null ||
    item.accommodation_parent_item_id === undefined ||
    (includeComplimentary && item.accommodation_parent_item_id && (includeCancelled || item.status !== 'cancelled')),
  )
}

export function isRoundTripAirportTransfer(airportTransfers: Array<App.OrderTransferItem>): boolean {
  const filteredTransferItems = filterAirportTransfers(airportTransfers)

  // First iteration of airport transfer updates only handles one way or return transfer.
  // I.e. the ones with the form on the experience page.
  if (filteredTransferItems.length > 2) {
    return false
  }

  const toHotelItems = filteredTransferItems.filter(item => item.type === 'AIRPORT-TO-HOTEL')
  const toAirportItems = filteredTransferItems.filter(item => item.type === 'HOTEL-TO-AIRPORT')

  return !(toHotelItems.length > 2 || toAirportItems.length > 2)
}

export function isAirportTransferDateInNextTwoWeeks(transferDate: string) {
  const currentDate = moment()
  const targetDate = moment().add(14, 'days')

  return moment(transferDate) >= currentDate && moment(transferDate) <= targetDate
}

export function airportTransferNoPickupDetailsMessage(transferItem: App.OrderTransferItem, showAirportTransfersUpdate: boolean) {
  if (transferItem.pickupDate && transferItem.pickupTime) {
    const intl = getCurrentIntl()
    return intl.formatMessage(
      {
        id: 'HcBKx8hQNmhCc13llAab',
        defaultMessage: '{date} · {time}',
        description: 'Airport transfer pickup date and time summary on the my escapes bookings page',
      },
      {
        date: moment(transferItem.pickupDate).format(DMY_CASUAL_LONG_FORMAT),
        time: moment(transferItem.pickupTime, SHORT_TIME_FORMAT_24_HOUR).format(SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE),
      },
    )
  }
  if (airportTransferInfoMissing(transferItem) && showAirportTransfersUpdate) {
    return 'Estimated pickup time provided once form is submitted'
  }
  return 'Your pick-up time will be confirmed within 7 days of your transfer date'
}

// ICS vendor IDs are the same in test and prod.
export const ICS_VENDOR_IDS = [
  '0012y00000W105xAAB',
  '0012y00000dbTjvAAE',
  '0010I000026fcwcQAA',
  '0010I00002J0Bb0QAF',
  '0012y00000jypSJAAY',
  '0012y00000W0foQAAR',
  '0012y00000ddqE4AAI',
]

export const ICS_CONTACT_NUMBERS: Record<string, { number: string, humanReadable: string }> = {
  Thailand: {
    number: '6281139603800',
    humanReadable: '+62 811 3960 3800',
  },
  Vietnam: {
    number: '6281139606800',
    humanReadable: '+62 811 3960 6800',
  },
  General: {
    number: '6281139606800',
    humanReadable: '+62 811 3960 6800',
  },
}

export const BALI_TRANSFER_SLUGS = [
  'bali-private-denpasar-airport-transfers',
  'bali-private-airport-transfers-for-accommodation-packages',
  'bali-denpasar-private-airport-transfers',
  'private-airport-transfers-for-bali-accommodation-packages',
  'bali-premium-private-denpasar-airport-transfers',
]

export function isBaliTransferSlug(slug?: string): boolean {
  return slug ? BALI_TRANSFER_SLUGS.includes(slug) : false
}

export function isBaliHotelToHotelTransfer(
  isBaliTransfer: boolean,
  placeId: string,
): boolean {
  return isBaliTransfer && placeId !== 'DPS'
}

export const ICS_AIRPORT_CODES = [
  // Bali, Indonesia
  { code: 'DPS', country: 'Indonesia', airportName: 'Ngurah Rai International Airport (Denpasar)' },

  // Singapore
  { code: 'SIN', country: 'Singapore', airportName: 'Changi Airport' },

  // Hong Kong
  { code: 'HKG', country: 'Hong Kong', airportName: 'Hong Kong International Airport' },

  // Malaysia
  { code: 'KUL', country: 'Malaysia', airportName: 'Kuala Lumpur International Airport' },
  { code: 'PEN', country: 'Malaysia', airportName: 'Penang International Airport' },
  { code: 'BKI', country: 'Malaysia', airportName: 'Kota Kinabalu International Airport' },
  { code: 'LGK', country: 'Malaysia', airportName: 'Langkawi International Airport' },
  { code: 'KCH', country: 'Malaysia', airportName: 'Kuching International Airport' },
  { code: 'JHB', country: 'Malaysia', airportName: 'Senai International Airport (Johor Bahru)' },
  { code: 'SZB', country: 'Malaysia', airportName: 'Sultan Abdul Aziz Shah Airport (Subang)' },
  { code: 'AOR', country: 'Malaysia', airportName: 'Sultan Abdul Halim Airport (Alor Setar)' },
  { code: 'KBR', country: 'Malaysia', airportName: 'Sultan Ismail Petra Airport (Kota Bharu)' },
  { code: 'TGG', country: 'Malaysia', airportName: 'Sultan Mahmud Airport (Kuala Terengganu)' },
  { code: 'KUA', country: 'Malaysia', airportName: 'Sultan Haji Ahmad Shah Airport (Kuantan)' },
  { code: 'LBU', country: 'Malaysia', airportName: 'Labuan Airport' },
  { code: 'IPH', country: 'Malaysia', airportName: 'Sultan Azlan Shah Airport (Ipoh)' },

  // Thailand
  { code: 'BKK', country: 'Thailand', airportName: 'Suvarnabhumi Airport (Bangkok)' },
  { code: 'DMK', country: 'Thailand', airportName: 'Don Mueang International Airport (Bangkok)' },
  { code: 'CNX', country: 'Thailand', airportName: 'Chiang Mai International Airport' },
  { code: 'HKT', country: 'Thailand', airportName: 'Phuket International Airport' },
  { code: 'HDY', country: 'Thailand', airportName: 'Hat Yai International Airport' },
  { code: 'KBV', country: 'Thailand', airportName: 'Krabi International Airport' },
  { code: 'USM', country: 'Thailand', airportName: 'Samui Airport (Ko Samui)' },
  { code: 'UTP', country: 'Thailand', airportName: 'U-Tapao International Airport (Rayong/Pattaya)' },
  { code: 'CEI', country: 'Thailand', airportName: 'Chiang Rai International Airport' },
  { code: 'URT', country: 'Thailand', airportName: 'Surat Thani International Airport' },
  { code: 'UTH', country: 'Thailand', airportName: 'Udon Thani International Airport' },

  // Vietnam
  { code: 'HAN', country: 'Vietnam', airportName: 'Noi Bai International Airport (Hanoi)' },
  { code: 'SGN', country: 'Vietnam', airportName: 'Tan Son Nhat International Airport (Ho Chi Minh City)' },
  { code: 'DAD', country: 'Vietnam', airportName: 'Da Nang International Airport' },
  { code: 'CXR', country: 'Vietnam', airportName: 'Cam Ranh International Airport (Nha Trang)' },
  { code: 'HUI', country: 'Vietnam', airportName: 'Phu Bai International Airport (Hue)' },
  { code: 'HPH', country: 'Vietnam', airportName: 'Cat Bi International Airport (Hai Phong)' },
  { code: 'VCA', country: 'Vietnam', airportName: 'Can Tho International Airport' },
  { code: 'PQC', country: 'Vietnam', airportName: 'Phu Quoc International Airport' },
  { code: 'VCL', country: 'Vietnam', airportName: 'Chu Lai Airport (Quang Nam)' },
  { code: 'VDO', country: 'Vietnam', airportName: 'Van Don International Airport' },
]
